import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, User, Phone, Mail, ChevronDown, DollarSign, X } from 'lucide-react';
import {
  useSubgroupMembers,
  useAddSubgroupMember,
  useUpdateSubgroupMember,
  useSubgroupMemberPayments,
} from '../hooks/useSubgroupMembers.ts';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'relocated', label: 'Relocated' },
  { value: 'deactivated', label: 'Deactivated' },
];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  relocated: 'bg-yellow-100 text-yellow-800',
  deactivated: 'bg-red-100 text-red-800',
};

function TenantPaymentsModal({ memberId, tenantId, tenantName, tenantLabel, onClose }) {
  const { data, isLoading } = useSubgroupMemberPayments(memberId, tenantId);
  const payments = data?.data ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{tenantLabel} Payments</h3>
            <p className="text-sm text-gray-500">{tenantName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 max-h-80 overflow-y-auto">
          {isLoading ? (
            <p className="text-center text-gray-400 py-4">Loading payments…</p>
          ) : payments.length === 0 ? (
            <p className="text-center text-gray-400 py-4">No individual payments recorded yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {payments.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">KES {Number(p.amount).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{p.description || 'Payment'}</p>
                  </div>
                  <span className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SubgroupMembersModal({ member, tenantLabel = 'Tenant', onClose }) {
  const memberId = member?.id;
  const { data, isLoading } = useSubgroupMembers(memberId);
  const addTenant = useAddSubgroupMember(memberId);
  const updateTenant = useUpdateSubgroupMember(memberId);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', phone_number: '', email: '' });
  const [viewPaymentsFor, setViewPaymentsFor] = useState(null);

  const tenants = data?.data ?? [];

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await addTenant.mutateAsync(newTenant);
      toast.success(`${tenantLabel} added successfully`);
      setShowAddForm(false);
      setNewTenant({ name: '', phone_number: '', email: '' });
    } catch (err) {
      toast.error(err?.response?.data?.errors ? JSON.stringify(err.response.data.errors) : 'Failed to add ' + tenantLabel);
    }
  };

  const handleStatusChange = async (tenant, status) => {
    try {
      await updateTenant.mutateAsync({ id: tenant.id, status });
      toast.success(`${tenantLabel} status updated`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {tenantLabel}s — {member?.name}
            </h3>
            <p className="text-sm text-gray-500">House No. {member?.member_no}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <p className="text-center text-gray-400 py-6">Loading…</p>
          ) : tenants.length === 0 && !showAddForm ? (
            <p className="text-center text-gray-400 py-6">No {tenantLabel.toLowerCase()}s yet. Add one below.</p>
          ) : (
            <ul className="divide-y divide-gray-100 mb-4">
              {tenants.map((t) => (
                <li key={t.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                      {t.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t.name}</p>
                      <p className="text-xs text-gray-500">{t.phone_number}{t.email ? ` • ${t.email}` : ''}</p>
                      {t.deactivated_at && (
                        <p className="text-xs text-red-400">Since {new Date(t.deactivated_at).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-12 sm:ml-0">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-600'}`}>
                      {t.status}
                    </span>
                    <select
                      value={t.status}
                      onChange={(e) => handleStatusChange(t, e.target.value)}
                      className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setViewPaymentsFor(t)}
                      className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                      title={`View ${tenantLabel} Payments`}
                    >
                      <DollarSign size={13} /> Payments
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Add Form */}
          {showAddForm && (
            <form onSubmit={handleAdd} className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3 mt-2">
              <h4 className="text-sm font-semibold text-gray-800">Add New {tenantLabel}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Full Name *</label>
                  <input
                    required
                    value={newTenant.name}
                    onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                    placeholder="John Doe"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number *</label>
                  <input
                    required
                    value={newTenant.phone_number}
                    onChange={(e) => setNewTenant({ ...newTenant, phone_number: e.target.value })}
                    placeholder="0712345678"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email (optional)</label>
                  <input
                    value={newTenant.email}
                    onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                    placeholder="john@email.com"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={addTenant.isPending}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  {addTenant.isPending ? 'Adding…' : `Add ${tenantLabel}`}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!showAddForm && (
          <div className="p-4 border-t flex justify-end">
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              <Plus size={15} /> Add {tenantLabel}
            </button>
          </div>
        )}
      </div>

      {/* Payments sub-modal */}
      {viewPaymentsFor && (
        <TenantPaymentsModal
          memberId={memberId}
          tenantId={viewPaymentsFor.id}
          tenantName={viewPaymentsFor.name}
          tenantLabel={tenantLabel}
          onClose={() => setViewPaymentsFor(null)}
        />
      )}
    </div>
  );
}
